/**
 * Tree Plugin Styles
 *
 * Scoped to tbw-grid using CSS nesting for light DOM compatibility.
 */
@layer tbw-plugins {
  tbw-grid {
    /* Expander column data cells - always first, borderless end edge */
    .cell[data-field='__tbw_expander'] {
      border-inline-end: none !important;
      padding: 0;
      display: flex;
      align-items: center;
      justify-content: flex-start;
    }

    /* Expander column header - completely hidden, next column spans both */
    .header-row .cell[data-field='__tbw_expander'] {
      display: none;
    }

    .header-row .cell[data-field='__tbw_expander'] + .cell {
      grid-column: 1 / 3;
    }

    /* Tree cell wrapper with depth-based indentation */
    .tree-cell-wrapper {
      display: inline-flex;
      align-items: center;
      /* Prevent baseline differences between tree-toggle (inline-flex) and
         tree-spacer (inline-block) from causing inconsistent row heights,
         which would trigger a rowHeight oscillation loop in virtualization. */
      vertical-align: middle;
      /* Override cell > * { overflow: hidden } which clips indented tree content */
      overflow: visible;
      /* Indent must be >= toggle icon width to keep leaf nodes aligned */
      padding-inline-start: calc(
        var(--tbw-tree-depth, 0) * var(--tbw-tree-indent-width, var(--tbw-tree-toggle-size, 1.25em))
      );
    }

    /* Tree expander cell with indentation */
    .tree-expander {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      width: 100%;
      height: 100%;
      box-sizing: border-box;
    }

    .tree-toggle {
      cursor: pointer;
      user-select: none;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      /* Explicit size ensures consistent spacing with tree-spacer */
      width: var(--tbw-tree-toggle-size, 1.25em);
      height: var(--tbw-tree-toggle-size, 1.25em);
      flex-shrink: 0;
    }

    .tree-toggle:hover {
      color: var(--tbw-tree-accent, var(--tbw-color-accent));
    }

    /* SC 2.5.8 Target Size (Minimum) applies to every pointer type, but a 24px
       box would widen every indentation level. The overlay grows the target
       instead of the box, so density is untouched. */
    .tree-toggle {
      position: relative;

      &::after {
        content: '';
        position: absolute;
        inset-block-start: 50%;
        inset-inline-start: 50%;
        translate: -50% -50%;
        inline-size: var(--tbw-touch-target-min, 24px);
        block-size: var(--tbw-touch-target-min, 24px);
      }
    }

    .tree-spacer {
      /* Must match tree-toggle width and height for alignment */
      width: var(--tbw-tree-toggle-size, 1.25em);
      height: var(--tbw-tree-toggle-size, 1.25em);
      display: inline-block;
      flex-shrink: 0;
    }

    /* The spacer occupies the toggle slot on leaf rows, so it must grow with the
       toggle — otherwise leaf rows sit narrower than expandable ones on touch. */
    @media (pointer: coarse) {
      .tree-toggle,
      .tree-spacer {
        min-width: var(--tbw-touch-target-min, 24px);
        min-height: var(--tbw-touch-target-min, 24px);
      }
    }

    /* Lazy-loading indicator — core's small spinner (`.tbw-spinner--small`)
       resized to the toggle slot so swapping toggle ↔ spinner doesn't reflow
       the row. Only the size token is overridden; all other spinner theming
       (`--tbw-spinner-color`, `--tbw-spinner-track-color`) still applies. */
    .tree-loading {
      --tbw-spinner-size: var(--tbw-tree-toggle-size, 1.25em);
      flex-shrink: 0;
    }

    /* Slide animation for child rows appearing */
    .data-grid-row.tbw-tree-slide-in {
      animation: tbw-tree-slide-in var(--tbw-animation-duration, 200ms) var(--tbw-animation-easing, ease-out) forwards;
    }

    /* Fade animation for child rows appearing */
    .data-grid-row.tbw-tree-fade-in {
      animation: tbw-tree-fade-in var(--tbw-animation-duration, 200ms) var(--tbw-animation-easing, ease-out) forwards;
    }
  }

  @keyframes tbw-tree-slide-in {
    from {
      opacity: 0;
      transform: translateX(-8px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes tbw-tree-fade-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
} /* End @layer tbw-plugins */
